import { Input } from '@components/_ui/Input'
import { useOnClickOutside } from '@hooks/useOnClickOutside'
import debounce from 'lodash.debounce'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import SmartySDK from 'smartystreets-javascript-sdk'

import s from './Autocomplete.module.scss'
import type { AutocompleteProps } from './Autocomplete.props'

const SmartyCore = SmartySDK.core
const Lookup = SmartySDK.internationalAddressAutocomplete.Lookup

const secretKey = '225601782703998038'
const credentials = new SmartyCore.SharedCredentials(secretKey)

const clientBuilder = new SmartyCore.ClientBuilder(credentials).withLicenses(['international-autocomplete-v2-cloud'])
const client = clientBuilder.buildInternationalAddressAutocompleteClient()

const country = 'CAN'

type Entry = { addressId: string; addressText: string, entries?: number }

export function Autocomplete({ initialValue = '', onChange }: AutocompleteProps) {
  const ref = useRef<any>(null)

  const [inputValue, setInputValue] = useState(initialValue)
  const [entries, setEntries] = useState<Entry[]>([])
  const [open, setOpen] = useState(false)

  const getEntries = async ({ search, addressId }: { search?: string, addressId?: string }) => {
    if ((search !== undefined && !search) || (addressId !== undefined && !addressId)) return

    try {
      // @ts-expect-error libs types is bullshit
      const summaryLookup = new Lookup({
        country,
        ...(search ? { search } : {}),
        ...(addressId ? { addressId } : {})
      })
      summaryLookup.includeOnlyAdministrativeArea = 'ON'
      const res = await client.send(summaryLookup)
      const addresses = (res?.result?.every((item: any) => !!item?.entries) ? res?.result : []) as any[]

      if (addresses.length) {
        setEntries(addresses)
        setOpen(true)
      } else {
        return res?.result?.[0]
      }
    } catch (err) {
      console.log('err', err)
    }
  }

  const debouncedCallback = useCallback(debounce(getEntries, 300), [])

  const handleInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value

    if (!value.trim()) {
      onChange('')
    }

    setInputValue(value)
    debouncedCallback({ search: value.trim() })
  }

  const selectEntries = async (entry: Entry) => {
    setOpen(false)

    if (entry?.entries === 1) {
      setInputValue(entry?.addressText)
    }

    const res = await getEntries({ addressId: entry?.addressId })

    if (res) {
      onChange({ ...res, addressText: entry?.addressText })
    }
  }

  useOnClickOutside(ref, () => setOpen(false))

  useEffect(() => {
    if (!initialValue) return

    setInputValue(initialValue)
  }, [initialValue])

  return (
    <div
      ref={ref}
      className={s.wrapper}
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder="Enter address"
      />

      {(open && !!entries.length) && (
        <ul className={s.list}>
          {entries.map((item) => (
            <li
              key={item.addressId}
              className={s.listItem}
              onClick={() => selectEntries(item)}
            >
              {item.addressText}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
